/**
 * A LAPDETEKTÁLÁS VARRATA — `scanic` mögötte, de nem rajta keresztül (D14).
 *
 * ── MIÉRT VARRAT, ÉS NEM KÖZVETLEN HÍVÁS ────────────────────────────────
 *
 * A `scanic` MIT-licences, <100 KB, és jól dolgozik — de **67 csillag és egy
 * fenntartó** áll mögötte (`szkenneles-terv.md` §5). Ez vállalható kockázat,
 * mert a felülete kicsi: kép be, sarkok ki. Pontosan ezért kell varrat — egy
 * ilyen felületet leváltani egy nap, ha a hívások EGY fájlban vannak, és egy
 * hét, ha húsz komponensben.
 *
 * A visszaút nem elméleti: az OpenCV.js ugyanezt tudja, csak 8–30 MB-ért.
 *
 * ── AMIT A VARRAT SZÁNDÉKOSAN NEM ENGED ÁT ──────────────────────────────
 *
 * A `scanic` saját típusait (`CornerPoints`, `ScannerResult`). Ha azok
 * átszivárognának a komponensekbe, a varrat csak látszólag lenne varrat: a
 * csere ugyanúgy minden hívót érintene, csak eggyel arrébb.
 *
 * ── A BETÖLTÉS KÉSLELTETETT, ÉS EZ MÉRHETŐ KÜLÖNBSÉG ────────────────────
 *
 * A WASM-mag akkor töltődik be, amikor a kamera ELŐSZÖR megnyílik — nem az
 * app indulásakor. Az ügyfelek többsége soha nem fotóz: a hiánylistát nézi
 * meg, vagy PDF-et tölt fel. Nekik ez a kód sosem tölt le.
 */

import { isPlausiblePage } from './page-plausibility';

/** Egy négyszög a képen, képpontban. Az origó a bal felső sarok. */
export interface QuadCorners {
  topLeft: { x: number; y: number };
  topRight: { x: number; y: number };
  bottomRight: { x: number; y: number };
  bottomLeft: { x: number; y: number };
}

/**
 * EGY ELKÉSZÜLT OLDAL.
 *
 * ⚠️ A `cropped` NEM DIAGNOSZTIKA, HANEM A FELÜLETNEK SZÓL. Ha a lapélt nem
 * találtuk meg, a kép attól még használható — de az ügyfélnek ezt LÁTNIA kell
 * az áttekintő képernyőn („a lap szélét nem találtuk, így küldöd?"), mert ő az,
 * aki két koppintással újrafotózhatja. Egy néma degradálás pont azt venné el
 * tőle, amiért a képernyő van.
 *
 * Naplózni nincs hova: a kliensben nincs `logger.ts`, és a `console` tiltott
 * (`client/CLAUDE.md`). Ez nem hiányosság — egy böngészőbeli naplósort úgysem
 * olvasna el senki. A hibának a FELÜLETEN kell megjelennie.
 */
export interface CapturedPage {
  /** A kép, amit feltöltünk és az IndexedDB-be teszünk. */
  blob: Blob;
  /**
   * IGAZ, ha a lap széle biztos: kivágtuk és kiegyenesítettük, vagy az
   * ügyfél maga mondta, hogy a teljes kép a lap („Teljes kép", #89).
   * HAMIS, ha a kivágás elbukott, és a nyers képkocka ment tovább.
   */
  cropped: boolean;
  /**
   * AZ EREDETI FOTÓ (#89) — vágatlanul, hogy a vágás az áttekintőről újra
   * igazítható legyen. CSAK A TELEFONON él a küldésig (Viktor, 2026-11-02);
   * a szerverre a kivágott `blob` megy. Régebbi, visszatöltött sorozat
   * lapjain hiányozhat — azoknál a „Vágás" nem kínálható.
   */
  original?: Blob;
  /** A vágás sarkai az EREDETI fotó képpontjaiban (#89). */
  corners?: QuadCorners | null;
}

export interface DocumentScanner {
  /**
   * ÉLŐ KERESŐKÉP — hol van a lap. Kép NEM keletkezik, csak a négy sarok.
   *
   * `null` = nem találtuk meg a lapot. Ez a gyakori eset, és nem hiba: az
   * ügyfél épp mozgatja a telefont.
   */
  detect(frame: ImageData): Promise<QuadCorners | null>;

  /**
   * ELŐTÖLTÉS (#87) — a felismerő betöltése, MIELŐTT az első képkocka megjön.
   *
   * A `scanic` (JS + WASM) késleltetve töltődik; e nélkül az első
   * `detect` várja ki a letöltést és az inicializálást, és addig a keret
   * nem jelenik meg — az ügyfél ezt „lassan ismeri fel"-nek látja. A kamera
   * engedélykérése és indulása úgyis tart egy darabig: az idő ott van.
   * Opcionális, és némán bukik: a `detect` maga is megpróbálja újra.
   */
  warmUp?(): void;

  /**
   * EXPONÁLÁS — a lap kivágva, kiegyenesítve, JPEG-ként.
   *
   * ⚠️ `Blob`, NEM `ImageData` vagy canvas. Az iOS Safari lapja hat teljes
   * felbontású képkockától elszáll (`szkenneles-terv.md` §6); egy JPEG-`Blob`
   * tizedannyi memória, és ez az, ami a feltöltésre és az IndexedDB-be megy.
   *
   * `corners: null` esetén a TELJES képkocka megy tovább, vágás nélkül. Nem
   * hibaág: ha a lapélt nem találtuk meg, a nyers fotó még mindig jobb, mint
   * a semmi — a könyvelő elolvassa, és a kinyerés is megpróbálja.
   */
  capture(frame: ImageData, corners: QuadCorners | null): Promise<CapturedPage>;

  /**
   * AZ EREDETI FOTÓ KÓDOLÁSA (#89) — a teljes képkocka JPEG-ként, vágás nélkül.
   * Ez marad meg a telefonon, hogy a vágás újra igazítható legyen.
   */
  encode(frame: ImageData): Promise<Blob>;

  /**
   * AZ EREDETI FOTÓ VISSZAOLVASÁSA (#89) — az áttekintőről indított
   * újravágáshoz. A képkocka csak addig él a memóriában, amíg a vágó nézet
   * nyitva van (iOS-en hat teljes felbontású képkocka elég a lap bukásához).
   */
  decode(blob: Blob): Promise<ImageData>;
}

/**
 * ⚠️ EGYIK SZÁM SINCS MEGMÉRVE.
 *
 * A `maxProcessingDimension` a detektálás belső méretkorlátja: efölött a
 * `scanic` kicsinyít. A 640 azt fejezi ki, hogy „élő képen a sebesség
 * fontosabb, mint a sarok utolsó képpontja" — de hogy 480 vagy 800 lenne-e a
 * helyes, azt csak eszközökön mérve lehet megmondani.
 *
 * Amit a választás kockáztat: túl kicsi érték gyenge kontrasztnál elveszti a
 * lapélt, túl nagy pedig akadozó keresőképet ad. A második a láthatóbb hiba,
 * ezért inkább kicsi.
 */
const LIVE_DETECTION_DIMENSION = 640;

/**
 * A KIMENETI JPEG MINŐSÉGE.
 *
 * ⚠️ SZINTÉN MÉRETLEN, de itt van támpont: a `feltoltesi-korlatok-javaslat.md`
 * szerint a nyers telefonfotó JPEG-ben ~3 MB, és a kivágott-kiegyenesített
 * lap ennek töredéke. A 0,85 a szokásos „nem látszik a különbség" tartomány
 * teteje — lejjebb menni a kinyerés pontosságát kockáztatná, ami drágább,
 * mint a sávszélesség.
 */
const CAPTURE_JPEG_QUALITY = 0.85;

/** A `scanic` betöltésekor vagy futásakor keletkezett hiba. */
export class DocumentScannerUnavailableError extends Error {
  constructor(cause: unknown) {
    super(`A lapfelismerő nem indult el: ${String(cause)}`);
    this.name = 'DocumentScannerUnavailableError';
  }
}

/**
 * A `scanic`-ra épülő megvalósítás.
 *
 * A modult KÉSLELTETVE tölti be, és a példányt megjegyzi — a `Scanner`
 * osztály a WASM-példányt életben tartja a képkockák között, ami a
 * keresőképnél nem mindegy: egy hívásonkénti újrainicializálás a detektálást
 * használhatatlanul lassúvá tenné.
 */
export function createScanicScanner(): DocumentScanner {
  let scannerPromise: Promise<ScanicScanner> | null = null;

  const scanner = async (): Promise<ScanicScanner> => {
    if (!scannerPromise) {
      scannerPromise = (async () => {
        try {
          const module = (await import('scanic')) as unknown as ScanicModule;
          const instance = new module.Scanner({
            mode: 'detect',
            maxProcessingDimension: LIVE_DETECTION_DIMENSION,
          });
          await instance.initialize();
          return instance;
        } catch (error) {
          // A SIKERTELEN BETÖLTÉST NEM JEGYEZZÜK MEG. Enélkül egy pillanatnyi
          // hálózati hiba a kamera EGÉSZ élettartamára elrontaná a
          // detektálást — a következő megnyitás is a bukott ígéretet kapná.
          scannerPromise = null;
          throw new DocumentScannerUnavailableError(error);
        }
      })();
    }
    return scannerPromise;
  };

  return {
    warmUp() {
      scanner().catch(() => undefined);
    },

    async detect(frame) {
      const instance = await scanner();
      const result = await instance.scan(frame, { mode: 'detect' });
      if (!(result.success && result.corners)) {
        return null;
      }
      // A KÉTES NÉGYSZÖG NEM LAP (#88) — inkább „nem találtam", mint egy
      // rossz vágás. Lásd `page-plausibility.ts`.
      return isPlausiblePage(result.corners, frame.width, frame.height) ? result.corners : null;
    },

    async capture(frame, corners) {
      const canvas = document.createElement('canvas');

      if (corners) {
        try {
          const module = (await import('scanic')) as unknown as ScanicModule;
          const extracted = await module.extractDocument(frame, corners, { output: 'canvas' });
          if (extracted.output instanceof HTMLCanvasElement) {
            return { blob: await toJpeg(extracted.output), cropped: true };
          }
        } catch {
          // A KIVÁGÁS BUKÁSA NEM VISZI EL A FOTÓT. Az ügyfél megnyomta az
          // exponálót; ha most hibát kapna, a papír megvan, a kép nincs. A
          // nyers képkocka rosszabb, de HASZNÁLHATÓ — és a `cropped: false`
          // viszi a hírt a felületre, ahol az ügyfél dönthet az újrafotózásról.
        }
      }

      canvas.width = frame.width;
      canvas.height = frame.height;
      const context = canvas.getContext('2d');
      if (!context) {
        throw new DocumentScannerUnavailableError('a canvas 2d környezete nem érhető el');
      }
      context.putImageData(frame, 0, 0);
      return { blob: await toJpeg(canvas), cropped: false };
    },

    async encode(frame) {
      const canvas = document.createElement('canvas');
      canvas.width = frame.width;
      canvas.height = frame.height;
      const context = canvas.getContext('2d');
      if (!context) {
        throw new DocumentScannerUnavailableError('a canvas 2d környezete nem érhető el');
      }
      context.putImageData(frame, 0, 0);
      return toJpeg(canvas);
    },

    async decode(blob) {
      const image = await loadImage(blob);
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) {
        throw new DocumentScannerUnavailableError('a canvas 2d környezete nem érhető el');
      }
      try {
        context.drawImage(image, 0, 0);
        return context.getImageData(0, 0, canvas.width, canvas.height);
      } finally {
        // AZ `ImageBitmap` TELJES FELBONTÁSÚ — azonnal elengedjük, nem a
        // szemétgyűjtőre bízzuk. iOS-en néhány ilyen elég a lap bukásához.
        if ('close' in image && typeof image.close === 'function') {
          image.close();
        }
      }
    },
  };
}

/**
 * EGY JPEG BETÖLTÉSE RAJZOLHATÓ KÉPPÉ. A `createImageBitmap` a gyors út; ahol
 * nincs (régebbi iOS Safari), az `<img>` a padló.
 */
async function loadImage(
  blob: Blob
): Promise<CanvasImageSource & { width: number; height: number }> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(blob);
  }
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function toJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new DocumentScannerUnavailableError('a canvas nem adott JPEG-et'));
      },
      'image/jpeg',
      CAPTURE_JPEG_QUALITY
    );
  });
}

/**
 * A `scanic` felülete, ANNYIBAN, amennyit használunk.
 *
 * Saját deklaráció, mert a csomag típusai a `src/` alól jönnek, és egy
 * könyvtár belső útvonalára hivatkozni a build-láncot kötné hozzá. Amit itt
 * leírunk, az a SZERZŐDÉS — ha a csomag megváltozik, itt lesz fordítási hiba,
 * nem húsz komponensben.
 */
interface ScanicModule {
  Scanner: new (options?: Record<string, unknown>) => ScanicScanner;
  extractDocument: (
    image: ImageData,
    corners: QuadCorners,
    options?: { output?: 'canvas' }
  ) => Promise<{ output: unknown }>;
}

interface ScanicScanner {
  initialize(): Promise<void>;
  scan(
    image: ImageData,
    options?: Record<string, unknown>
  ): Promise<{ success: boolean; corners: QuadCorners | null }>;
}
